import { match, P } from "ts-pattern";
import { z } from "zod";
import { mapValues, nullToUndefined } from "../../../lib/functional";
import { StableValue } from "../../../lib/stable-value";
import { safeParse } from "../../../lib/zod";
import { responsiveValue } from "../../../common/schema";
import { Targets } from "../../../introspection";
import {
  ResourceSchema
} from "../../../resources";
import { Color } from "../../color";
import {
  ControlDefinition,
  serialize
} from "../../definition";
import * as StyleSchema from "./schema";
import { StyleControl } from "./style-control";
class Definition extends ControlDefinition {
  static type = "makeswift::controls::style";
  static Property = {
    Width: `${this.type}::property::width`,
    Margin: `${this.type}::property::margin`,
    Padding: `${this.type}::property::padding`,
    Border: `${this.type}::property::border`,
    BorderRadius: `${this.type}::property::border-radius`,
    TextStyle: `${this.type}::property::text-style`
  };
  static DefaultProperties = [
    this.Property.Width,
    this.Property.Margin
  ];
  static AllProperties = Object.values(this.Property);
  static get schema() {
    const type = z.literal(Definition.type);
    const properties = z.union([
      z.literal(this.Property.Width),
      z.literal(this.Property.Margin),
      z.literal(this.Property.Padding),
      z.literal(this.Property.Border),
      z.literal(this.Property.BorderRadius),
      z.literal(this.Property.TextStyle)
    ]);
    const config = z.object({
      properties: z.array(properties)
    });
    const dataSchema = (color) => z.object({
      width: responsiveValue(StyleSchema.width).optional(),
      margin: responsiveValue(StyleSchema.margin).optional(),
      padding: responsiveValue(StyleSchema.padding).optional(),
      border: responsiveValue(StyleSchema.borderSchema(color)).optional(),
      borderRadius: responsiveValue(StyleSchema.borderRadius).optional(),
      textStyle: responsiveValue(StyleSchema.textStyle).optional()
    });
    const data = dataSchema(ResourceSchema.colorData);
    const resolvedData = dataSchema(ResourceSchema.resolvedColorData);
    const value = data;
    const resolvedValue = z.string();
    const definition = z.object({
      type,
      config
    });
    return {
      type,
      config,
      definition,
      data,
      resolvedData,
      value,
      resolvedValue
    };
  }
  static deserialize(data) {
    if (data.type !== Definition.type) {
      throw new Error(
        `Style: expected type ${Definition.type}, got ${data.type}`
      );
    }
    const { config } = Definition.schema.definition.parse(data);
    return Style(config);
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    return {
      ...Definition.schema,
      data: Definition.schema.data,
      value: Definition.schema.value
    };
  }
  safeParse(data) {
    return safeParse(this.schema.data, data);
  }
  fromData(data) {
    return this.schema.data.optional().parse(data);
  }
  toData(value) {
    return value;
  }
  copyData(data, context) {
    if (data == null)
      return data;
    return {
      ...data,
      border: mapBorderSides(
        data.border,
        (side) => side.color == null ? side : {
          ...side,
          color: Color().copyData(side.color, context)
        }
      )
    };
  }
  resolveValue(data, resolver, stylesheet, control) {
    const resolvedColors = new Map(
      [...borderColors(data?.border)].map((color) => [
        color.swatchId,
        Color().resolveSwatch(nullToUndefined(color), resolver)
      ])
    );
    const resolvedColor = (swatchId) => swatchId ? resolvedColors.get(swatchId)?.readStable() : void 0;
    const resolvedStyle = (data2) => ({
      ...data2,
      border: mapBorderSides(data2.border, (side) => ({
        ...side,
        color: resolvedColor(side?.color?.swatchId)
      }))
    });
    const stableValue = StableValue({
      name: Definition.type,
      read: () => stylesheet.defineStyle(
        {
          properties: this.config.properties,
          styleData: data != null ? resolvedStyle(data) : {}
        },
        (boxModel) => control?.changeBoxModel(boxModel)
      ),
      deps: [...resolvedColors.values()]
    });
    return {
      ...stableValue,
      triggerResolve: () => Promise.all(
        [...resolvedColors.values()].map((r) => r.triggerResolve())
      )
    };
  }
  createInstance(sendMessage) {
    return new StyleControl(sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type
    });
  }
  introspect(data, target) {
    if (data == null || target.type !== Targets.Swatch.type) {
      return [];
    }
    return Object.values(data).flat().flatMap(
      (override) => match(override.value).with(
        P.union(
          { borderTop: { color: P.any } },
          { borderLeft: { color: P.any } },
          { borderBottom: { color: P.any } },
          { borderRight: { color: P.any } }
        ),
        (overrideValue) => [
          overrideValue.borderBottom?.color,
          overrideValue.borderTop?.color,
          overrideValue.borderLeft?.color,
          overrideValue.borderRight?.color
        ]
      ).otherwise(() => [null])
    ).flatMap((color) => target.introspect(color));
  }
}
function* borderColors(responsibeBorder) {
  for (const deviceBorder of responsibeBorder ?? []) {
    const sides = deviceBorder.value;
    for (const side of Object.values(sides)) {
      if (side?.color != null) {
        yield side?.color;
      }
    }
  }
}
function mapBorderSides(responsibeBorder, callback) {
  return responsibeBorder?.map((deviceBorder) => ({
    ...deviceBorder,
    value: mapValues(
      deviceBorder.value,
      (side) => side == null ? side : callback(side)
    )
  }));
}
class StyleDefinition extends Definition {
}
function Style(config) {
  return new StyleDefinition(
    config ?? {
      properties: StyleDefinition.DefaultProperties
    }
  );
}
Style.Default = StyleDefinition.DefaultProperties;
Style.All = StyleDefinition.AllProperties;
Style.Width = StyleDefinition.Property.Width;
Style.Margin = StyleDefinition.Property.Margin;
Style.Padding = StyleDefinition.Property.Padding;
Style.Border = StyleDefinition.Property.Border;
Style.BorderRadius = StyleDefinition.Property.BorderRadius;
Style.TextStyle = StyleDefinition.Property.TextStyle;
export {
  Style,
  StyleDefinition
};
//# sourceMappingURL=style.js.map